import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  Menu,
  X,
  Phone,
  ArrowRight,
  User,
  ChevronDown,
  LogOut,
  LayoutDashboard,
  Truck,
  Shield
} from "lucide-react";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "./ui/dropdown-menu";
import { COMPANY, NAV_LINKS } from "../data/mock";
import { useAuth } from "../context/AuthContext";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname]);

  const dashboardPath =
    user?.role === "admin"
      ? "/admin-dashboard"
      : user?.role === "driver"
      ? "/driver-dashboard"
      : "/customer-dashboard";

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-all duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur border-b border-slate-200 shadow-sm"
          : "bg-white border-b border-transparent"
      }`}
    >
      <div className="container-518 flex items-center justify-between h-[78px]">
        <Link to="/" className="flex items-center gap-3 group">
          <img
            src={COMPANY.logo}
            alt="518 Courier"
            className="w-12 h-12 rounded-full object-cover ring-2 ring-[#0f2847]/10 group-hover:ring-[#f59321]/40 transition"
          />
          <div className="leading-tight hidden sm:block">
            <div className="font-display font-extrabold text-[#0f2847] text-lg tracking-tight">
              518 <span className="text-[#f59321]">Courier</span>
            </div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
              Albany · Capital Region
            </div>
          </div>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-1">
          {NAV_LINKS.map((link) => (
            <NavLink
              key={link.path}
              to={link.path}
              end={link.path === "/"}
              className={({ isActive }) =>
                `relative px-4 py-2 text-sm font-semibold transition-colors ${
                  isActive ? "text-[#0f2847]" : "text-slate-600 hover:text-[#0f2847]"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {link.label}
                  <span
                    className={`absolute left-4 right-4 -bottom-0.5 h-[3px] rounded-full bg-[#f59321] transition-transform origin-left ${
                      isActive ? "scale-x-100" : "scale-x-0"
                    }`}
                  />
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Right side */}
        <div className="flex items-center gap-2 md:gap-3">
          <a
            href={`tel:${COMPANY.phone.replace(/[^0-9]/g, "")}`}
            className="hidden xl:flex items-center gap-2 text-sm font-semibold text-[#0f2847] hover:text-[#f59321] transition-colors"
          >
            <Phone className="w-4 h-4" />
            {COMPANY.phone}
          </a>

          {/* Sign-in dropdown OR account */}
          {!user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="hidden sm:inline-flex items-center gap-1.5 h-11 px-4 rounded-full border border-slate-200 text-sm font-semibold text-[#0f2847] hover:border-[#f59321] hover:text-[#f59321] transition-colors">
                  <User className="w-4 h-4" />
                  Sign in
                  <ChevronDown className="w-3.5 h-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>Sign in as</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={() => navigate("/login")}>
                  <User className="w-4 h-4 mr-2" /> Customer
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => navigate("/driver-login")}>
                  <Truck className="w-4 h-4 mr-2" /> Driver
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => navigate("/admin-login")}>
                  <Shield className="w-4 h-4 mr-2" /> Admin
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={() => navigate("/register")}>
                  New customer? Create account
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => navigate("/driver-signup")}>
                  Drive with 518
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="hidden sm:inline-flex items-center gap-2 h-11 px-3 rounded-full border border-slate-200 text-sm font-semibold text-[#0f2847] hover:border-[#f59321] transition-colors">
                  <span className="w-7 h-7 rounded-full bg-[#0f2847] text-white text-xs font-bold flex items-center justify-center">
                    {(user.name || user.email || "U").slice(0, 1).toUpperCase()}
                  </span>
                  <span className="max-w-[100px] truncate">{user.name}</span>
                  <ChevronDown className="w-3.5 h-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="capitalize">{user.role} account</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={() => navigate(dashboardPath)}>
                  <LayoutDashboard className="w-4 h-4 mr-2" /> Dashboard
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => navigate("/compliance-center")}>
                  <Shield className="w-4 h-4 mr-2" /> Compliance docs
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={() => { logout(); navigate("/"); }}>
                  <LogOut className="w-4 h-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          <Link to="/get-a-quote" className="hidden md:inline-flex">
            <Button className="btn-lift bg-[#f59321] hover:bg-[#e07f10] text-white font-semibold rounded-full px-5 h-11 shadow-md">
              Get a Quote
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>

          <button
            aria-label="Toggle menu"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden inline-flex items-center justify-center w-11 h-11 rounded-full border border-slate-200 text-[#0f2847] hover:bg-slate-50 transition"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        className={`lg:hidden overflow-hidden border-t border-slate-100 bg-white transition-[max-height,opacity] duration-300 ${
          open ? "max-h-[640px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="container-518 py-4 flex flex-col gap-1">
          {NAV_LINKS.map((link) => (
            <NavLink
              key={link.path}
              to={link.path}
              end={link.path === "/"}
              className={({ isActive }) =>
                `px-3 py-3 rounded-lg text-base font-semibold transition-colors ${
                  isActive ? "bg-[#0f2847] text-white" : "text-slate-700 hover:bg-slate-50"
                }`
              }
            >
              {link.label}
            </NavLink>
          ))}
          <div className="my-3 h-px bg-slate-100" />
          {!user ? (
            <>
              <Link to="/login" className="px-3 py-3 rounded-lg text-base font-semibold text-slate-700 hover:bg-slate-50">Customer sign in</Link>
              <Link to="/driver-login" className="px-3 py-3 rounded-lg text-base font-semibold text-slate-700 hover:bg-slate-50">Driver sign in</Link>
              <Link to="/admin-login" className="px-3 py-3 rounded-lg text-base font-semibold text-slate-700 hover:bg-slate-50">Admin sign in</Link>
              <Link to="/driver-signup" className="px-3 py-3 rounded-lg text-base font-semibold text-slate-700 hover:bg-slate-50">Drive with 518</Link>
            </>
          ) : (
            <>
              <Link to={dashboardPath} className="px-3 py-3 rounded-lg text-base font-semibold text-slate-700 hover:bg-slate-50">My Dashboard</Link>
              <Link to="/compliance-center" className="px-3 py-3 rounded-lg text-base font-semibold text-slate-700 hover:bg-slate-50">Compliance docs</Link>
              <button onClick={() => { logout(); navigate("/"); }} className="text-left px-3 py-3 rounded-lg text-base font-semibold text-slate-700 hover:bg-slate-50">Sign out</button>
            </>
          )}
          <Link to="/get-a-quote" className="mt-2">
            <Button className="w-full bg-[#f59321] hover:bg-[#e07f10] text-white font-semibold rounded-full h-12">
              Get a Quote
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </div>
      </div>
    </header>
  );
}
